import React, { useEffect, useState, useMemo } from "react";
import { FaSearch, FaCalendarAlt, FaFileExcel } from "react-icons/fa";
import { getData } from "../../../services/FetchNodeservices";
import * as XLSX from "xlsx";

export default function AdminAttendance() {
  const [attendance, setAttendance] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");

  const [filterType, setFilterType] = useState("today");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(true);

  /* ================= FETCH ================= */
  const fetchAttendance = async () => {
    setLoading(true);
    const result = await getData("adminattendance/view");
    if (result.status) setAttendance(result.data);
    setLoading(false);
  };

  useEffect(() => {
    fetchAttendance();
  }, []);

  /* ================= HELPERS ================= */
  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const formatTime = (d) =>
    d
      ? new Date(d).toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";

  const cleanDate = (d) => new Date(d).setHours(0, 0, 0, 0);

  /* ================= FILTER ================= */
  const applyFilter = () => {
    let today = cleanDate(new Date());
    let data = attendance;

    if (filterType === "today") {
      data = attendance.filter(
        (a) => cleanDate(a.punch_in_time) === today
      );
    }

    if (filterType === "yesterday") {
      let y = today - 86400000;
      data = attendance.filter(
        (a) => cleanDate(a.punch_in_time) === y
      );
    }

    if (filterType === "week") {
      let weekStart = today - 6 * 86400000;
      data = attendance.filter(
        (a) => cleanDate(a.punch_in_time) >= weekStart
      );
    }

    if (filterType === "custom" && fromDate && toDate) {
      const from = cleanDate(fromDate);
      const to = cleanDate(toDate);
      data = attendance.filter((a) => {
        const d = cleanDate(a.punch_in_time);
        return d >= from && d <= to;
      });
    }

    data = data.filter((item) =>
      `${item.first_name} ${item.last_name}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );

    setFiltered(data);
  };

  useEffect(() => {
    applyFilter();
  }, [attendance, search, filterType]);

  /* ================= GROUP ================= */
  const grouped = useMemo(() => {
    const group = {};
    filtered.forEach((item) => {
      const key = formatDate(item.punch_in_time);
      if (!group[key]) group[key] = [];
      group[key].push(item);
    });
    return group;
  }, [filtered]);

  const dateKeys = Object.keys(grouped);

  const getStatus = (item) =>
    item.punch_out_time ? "Logout" : "Login";

  /* ================= EXPORT TO EXCEL ================= */
  const exportData = () => {
    if (!filtered.length) return alert("No records to export");

    const dataToExport = filtered.map((item, idx) => ({
      "Sr No": idx + 1,
      "Employee Code": item.employee_code || "—",
      "Employee Name": `${item.first_name || ""} ${item.last_name || ""}`.trim(),
      "Date": item.punch_in_time
        ? new Date(item.punch_in_time).toLocaleDateString("en-IN", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
        : "—",
      "Punch In Time": item.punch_in_time
        ? new Date(item.punch_in_time).toLocaleTimeString("en-IN", {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "—",
      "Punch Out Time": item.punch_out_time
        ? new Date(item.punch_out_time).toLocaleTimeString("en-IN", {
            hour: "2-digit",
            minute: "2-digit",
          })
        : "—",
      "Status": item.punch_out_time ? "Logout" : "Login",
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);

    // Set custom column widths for aesthetic rendering
    ws["!cols"] = [
      { wch: 8 },  // Sr No
      { wch: 15 }, // Employee Code
      { wch: 25 }, // Employee Name
      { wch: 15 }, // Date
      { wch: 15 }, // Punch In Time
      { wch: 15 }, // Punch Out Time
      { wch: 12 }, // Status
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance");
    XLSX.writeFile(wb, "Attendance_Report.xlsx");
  };

  return (
    <div className="md:ml-72 px-4 md:px-6 pt-24 pb-8 min-h-screen text-slate-800">
      {/* ================= HEADER ================= */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 pb-2 border-b border-slate-150">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-800 tracking-tight">
            Attendance Logs
          </h2>
          <p className="text-xs text-slate-500 mt-1">Track and export daily employee working durations</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto items-center">
          <div className="relative w-full sm:w-64">
            <FaSearch className="absolute top-3.5 left-3 text-slate-400 text-sm" />
            <input
              type="text"
              placeholder="Search by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="glass-input pl-10 pr-3 py-2.5 rounded-xl outline-none transition-all w-full text-sm"
            />
          </div>

          <button
            onClick={exportData}
            className="
              inline-flex items-center justify-center gap-2
              px-4 py-2.5
              bg-emerald-600 text-white text-sm font-semibold
              rounded-xl shadow hover:bg-emerald-700 hover:shadow-lg transition-all duration-200
              w-full sm:w-auto
            "
          >
            <FaFileExcel /> Export Excel
          </button>
        </div>
      </div>

      {/* ================= FILTERS ================= */}
      <div className="flex flex-wrap gap-3 mb-5">
        {["today", "yesterday", "week", "custom"].map((type) => (
          <button
            key={type}
            onClick={() => setFilterType(type)}
            className={`px-4 py-2.5 rounded-xl text-xs font-semibold shadow-xs transition-all duration-200 ${
              filterType === type
                ? "bg-indigo-600 text-white shadow-md shadow-indigo-600/10"
                : "glass-btn text-slate-600 dark:text-slate-300"
            }`}
          >
            {type === "week" ? "Last 7 Days" : type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        ))}
      </div>

      {/* ================= CUSTOM RANGE ================= */}
      {filterType === "custom" && (
        <div className="flex flex-col sm:flex-row gap-3 mb-6 bg-white/50 backdrop-blur-xs p-4 rounded-2xl border border-slate-200/40 max-w-xl">
          <div className="flex flex-col flex-1 gap-1">
            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider pl-1">From Date</span>
            <input
              type="date"
              className="glass-input rounded-xl px-3 py-2 text-xs outline-none w-full"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="flex flex-col flex-1 gap-1">
            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider pl-1">To Date</span>
            <input
              type="date"
              className="glass-input rounded-xl px-3 py-2 text-xs outline-none w-full"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <button
            onClick={applyFilter}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl shadow font-semibold text-xs hover:bg-indigo-700 transition-colors self-end h-9"
          >
            Apply
          </button>
        </div>
      )}

      {/* ================= CONTENT ================= */}
      {loading ? (
        <div className="text-center text-sm font-semibold text-slate-500 py-10">Loading logs...</div>
      ) : (
        <div className="space-y-6">
          {dateKeys.map((date) => (
            <div key={date} className="glass-card overflow-hidden">
              {/* GROUP HEADER */}
              <div className="px-5 py-3.5 bg-slate-100/60 border-b border-slate-200/50 text-slate-800 font-bold text-xs flex items-center gap-2 tracking-wide uppercase">
                <FaCalendarAlt className="text-indigo-600" /> {date}
              </div>

              {/* TABLE WRAPPER */}
              <div className="overflow-x-auto">
                <table className="min-w-[600px] w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50/20 border-b border-slate-150 text-slate-500 font-bold uppercase tracking-wider text-[10px]">
                      <th className="px-6 py-3 text-left">Employee Name</th>
                      <th className="px-6 py-3 text-left">Time In</th>
                      <th className="px-6 py-3 text-left">Time Out</th>
                      <th className="px-6 py-3 text-center w-32">Status</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {grouped[date].map((item, idx) => (
                      <tr key={idx} className="hover:bg-slate-50/40 transition-colors border-b border-slate-100 last:border-0">
                        <td className="px-6 py-3.5 font-bold text-slate-800 whitespace-nowrap">
                          {item.first_name} {item.last_name}
                        </td>
                        <td className="px-6 py-3.5 text-slate-650 whitespace-nowrap">
                          {formatTime(item.punch_in_time)}
                        </td>
                        <td className="px-6 py-3.5 text-slate-650 whitespace-nowrap">
                          {formatTime(item.punch_out_time)}
                        </td>
                        <td className="px-6 py-3.5 text-center whitespace-nowrap">
                          <span
                            className={`px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full border
                              ${
                                getStatus(item) === "Login"
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200/40"
                                  : "bg-rose-50 text-rose-700 border-rose-200/40"
                              }`}
                          >
                            {getStatus(item)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {dateKeys.length === 0 && (
            <div className="text-center text-slate-400 py-12 text-sm bg-white/50 rounded-2xl border border-slate-200/30">
              No attendance logs found for the selected range.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
